import React from 'react';
import { ErrorBoundary as AppErrorBoundary } from './ErrorBoundary';

interface PageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class PageErrorBoundary extends React.Component<
  { children: React.ReactNode },
  PageErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Page error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-xl bg-red-50 border border-red-200 p-6 m-4">
          <h2 className="text-lg font-bold text-red-800 mb-2">This page encountered an error</h2>
          <p className="text-sm text-red-700 font-mono bg-red-100 rounded-lg p-3 break-words">
            {this.state.error?.message || 'Unknown error'}
          </p>
          {this.state.error?.stack && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-red-500 font-medium">Stack trace</summary>
              <pre className="mt-2 p-3 bg-white rounded-lg overflow-auto max-h-48 text-gray-600 border border-red-100">
                {this.state.error.stack}
              </pre>
            </details>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
